import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Check, Copy, Shield } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';

export const LEARNING_REPORT_DOCS_PATH = '/docs/relatorio-geppetto';

export function isLearningReportDocsPath() {
  try {
    const path = (window.location.pathname || '/').replace(/\/$/, '') || '/';
    return path === LEARNING_REPORT_DOCS_PATH || path.endsWith(LEARNING_REPORT_DOCS_PATH);
  } catch {
    return false;
  }
}

const EXAMPLE_JSON = `{
  "activity": {
    "cmid": 42,
    "resourceid": 7,
    "courseid": 3,
    "coursename": "Introdução ao Moodle",
    "name": "Módulo 3: LMS e escola",
    "layoutversion": 4,
    "checkpointcount": 2,
    "quizcount": 1,
    "grademax": 100,
    "groupid": 0,
    "generated": 1759090000
  },
  "summary": {
    "studentcount": 2,
    "checkpointscompletedstudents": 1,
    "commentcount": 0,
    "commentauthors": 0
  },
  "checkpoints": [
    {
      "index": 0,
      "label": "Vídeo 1 (meta 75%)",
      "widgetid": "ck-video-1",
      "mode": "video",
      "videopercent": 75,
      "completed": 1,
      "percent": 50
    },
    {
      "index": 1,
      "label": "Permanência 1",
      "widgetid": "ck-time-1",
      "mode": "time",
      "videopercent": -1,
      "completed": 1,
      "percent": 50
    }
  ],
  "quizzes": [
    {
      "quizid": "quiz-1",
      "graded": true,
      "weight": 100,
      "passingpercent": 70,
      "studentsattempted": 1,
      "attemptcount": 2,
      "averagebest": 80,
      "passed": 1
    }
  ],
  "questions": [
    {
      "quizid": "quiz-1",
      "questionid": "q1",
      "prompt": "O que o Moodle organiza?",
      "correctindex": 1,
      "answered": 1,
      "correctcount": 1,
      "choices": [
        { "index": 0, "label": "Só arquivos", "count": 0, "correct": false },
        { "index": 1, "label": "Cursos e participantes", "count": 1, "correct": true }
      ]
    }
  ],
  "students": [
    {
      "userid": 15,
      "fullname": "Ana Exemplo",
      "email": "ana.exemplo@escola.test",
      "idnumber": "2026001",
      "checkpointsdone": 2,
      "checkpointstotal": 2,
      "allcheckpoints": true,
      "modulepercent": 80,
      "attemptcount": 2,
      "commentcount": 0,
      "status": "completed",
      "lastinteraction": 1759080000,
      "missing": "",
      "videodone": 1,
      "videototal": 1,
      "wrongquestions": ""
    },
    {
      "userid": 16,
      "fullname": "Bruno Exemplo",
      "email": "bruno.exemplo@escola.test",
      "idnumber": "",
      "checkpointsdone": 0,
      "checkpointstotal": 2,
      "allcheckpoints": false,
      "modulepercent": -1,
      "attemptcount": 0,
      "commentcount": 0,
      "status": "notstarted",
      "lastinteraction": 0,
      "missing": "Vídeo 1 (meta 75%), Permanência 1",
      "videodone": 0,
      "videototal": 1,
      "wrongquestions": ""
    }
  ],
  "blocks": [],
  "objects": [
    {
      "kind": "checkpoint",
      "id": "0",
      "label": "Vídeo 1 (meta 75%)",
      "users": 1,
      "total": 2
    }
  ]
}`;

const EXAMPLE_URL = 'https://SEU-MOODLE/webservice/rest/server.php?wstoken=SEU_TOKEN&wsfunction=mod_gepettoresource_get_learning_report&moodlewsrestformat=json&cmid=42&groupid=0';

function CopyBlock({ text, label }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onCopy}
        className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-lg border border-slate-600 bg-slate-800 px-2 py-1 text-xs font-medium text-slate-100 hover:bg-slate-700"
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
        {copied ? 'OK' : label}
      </button>
      <pre className="overflow-x-auto rounded-xl bg-slate-950 p-4 pr-24 text-xs leading-relaxed text-slate-100">{text}</pre>
    </div>
  );
}

export default function LearningReportDocs() {
  const { i18n } = useTranslation('builder');
  const pt = (i18n.language || 'pt').toLowerCase().startsWith('pt');
  const copyLabel = pt ? 'Copiar' : 'Copy';

  return (
    <div className="geppetto-report-docs min-h-screen bg-slate-50 text-slate-800">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-sky-700">
              <BookOpen size={18} />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-sky-700">Geppetto</p>
              <h1 className="text-lg font-extrabold tracking-tight text-slate-900">
                {pt ? 'API do Relatório Geppetto' : 'Geppetto report API'}
              </h1>
            </div>
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-8 px-5 py-8">
        <p className="text-base leading-relaxed text-slate-600">
          {pt
            ? 'Esta página explica como um painel de BI lê o acompanhamento dos alunos de uma atividade Arquivo Gepetto. Os dados ficam no Moodle. Esta página não devolve alunos, notas nem token.'
            : 'This page explains how a BI dashboard reads student follow-up for one Gepetto Resource activity. The data stays in Moodle. This page does not return students, grades or a token.'}
        </p>

        <section className="rounded-2xl border border-sky-200 bg-sky-50 p-5">
          <h2 className="text-sm font-bold text-sky-900">
            {pt ? 'Isto não é um xAPI / LRS' : 'This is not an xAPI / LRS'}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-sky-950">
            {pt
              ? 'A chamada devolve um JSON do Relatório Geppetto (checkpoints, vídeo pela meta assistida, quizzes e comentários). Não é um statement xAPI e o Geppetto não guarda o histórico do aluno.'
              : 'The call returns the Geppetto report as JSON (checkpoints, video by watch target, quizzes and comments). It is not an xAPI statement, and Geppetto does not store the learner history.'}
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">{pt ? 'A chamada' : 'The call'}</h2>
          <ul className="space-y-2 text-sm leading-relaxed text-slate-700">
            <li><span className="font-semibold">wsfunction:</span> mod_gepettoresource_get_learning_report</li>
            <li><span className="font-semibold">{pt ? 'Serviço' : 'Service'}:</span> Geppetto learning report (gepettoresource_learning_report)</li>
            <li><span className="font-semibold">{pt ? 'Capability' : 'Capability'}:</span> mod/gepettoresource:viewreports</li>
            <li><span className="font-semibold">cmid:</span> {pt ? 'ID da atividade no curso (obrigatório).' : 'Activity id in the course (required).'}</li>
            <li><span className="font-semibold">groupid:</span> {pt ? '0 para todos os alunos visíveis. Outro valor limita ao grupo, se o usuário do token puder vê-lo.' : '0 for every visible student. Another value limits the slice to that group, if the token user can see it.'}</li>
          </ul>
          <CopyBlock text={EXAMPLE_URL} label={copyLabel} />
          <p className="text-sm text-slate-500">
            {pt
              ? 'Troque SEU-MOODLE, SEU_TOKEN e cmid. O protocolo REST do Moodle precisa estar habilitado. Sem token válido a chamada não responde o relatório.'
              : 'Replace SEU-MOODLE, SEU_TOKEN and cmid. Moodle REST protocol must be enabled. Without a valid token the call does not return the report.'}
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">{pt ? 'Criar o token no Moodle' : 'Create the Moodle token'}</h2>
          <ol className="list-decimal space-y-2 pl-5 text-sm leading-relaxed text-slate-700">
            <li>{pt ? 'Entre como administrador do site.' : 'Sign in as site administrator.'}</li>
            <li>{pt ? 'Administração do site → Servidor → Web services → Visão geral, e conclua: ativar web services e ativar o protocolo REST.' : 'Site administration → Server → Web services → Overview, then enable web services and the REST protocol.'}</li>
            <li>{pt ? 'Serviços externos → abra “Geppetto learning report”. Ele nasce desativado. Ative o serviço.' : 'External services → open “Geppetto learning report”. It ships disabled. Enable it.'}</li>
            <li>{pt ? 'Na mesma tela, em usuários autorizados, inclua só a conta que o BI vai usar. O serviço não aceita qualquer usuário do site.' : 'On the same screen, under authorised users, add only the account the BI will use. The service does not accept every site user.'}</li>
            <li>{pt ? 'Essa conta precisa da capability mod/gepettoresource:viewreports nos cursos que o BI vai ler. Professor, professor editor e gerente já a recebem. Uma conta de serviço deve receber um papel com essa capability no curso, sem ser administrador do site inteiro.' : 'That account needs mod/gepettoresource:viewreports in the courses the BI will read. Teacher, editing teacher and manager already have it. A service account should get a role with that capability in the course, not site administrator.'}</li>
            <li>{pt ? 'Administração do site → Servidor → Web services → Gerenciar tokens. Crie um token para essa conta, no serviço Geppetto learning report.' : 'Site administration → Server → Web services → Manage tokens. Create a token for that account on the Geppetto learning report service.'}</li>
            <li>{pt ? 'Guarde o token no cofre do BI. Não cole o token em slide, e-mail, repositório público nem nesta página.' : 'Store the token in the BI vault. Do not paste it into a slide, email, public repository or this page.'}</li>
          </ol>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">{pt ? 'JSON de exemplo' : 'Example JSON'}</h2>
          <p className="text-sm leading-relaxed text-slate-600">
            {pt
              ? 'Nomes e e-mails abaixo são fictícios. No Moodle real, students traz o nome, o e-mail e a matrícula dos alunos ativos que entram nos relatórios de conclusão.'
              : 'Names and emails below are fictional. On a real Moodle, students includes the name, email and id number of active students included in completion reports.'}
          </p>
          <CopyBlock text={EXAMPLE_JSON} label={copyLabel} />
          <ul className="space-y-2 text-sm leading-relaxed text-slate-700">
            <li><span className="font-semibold">status:</span> {pt ? 'notstarted, inprogress ou completed.' : 'notstarted, inprogress or completed.'}</li>
            <li><span className="font-semibold">videopercent:</span> {pt ? 'meta de assistência do checkpoint (por exemplo 75). −1 quando o checkpoint não é vídeo.' : 'watch target for that checkpoint (for example 75). −1 when the checkpoint is not a video.'}</li>
            <li><span className="font-semibold">videodone / videototal:</span> {pt ? 'quantos vídeos aquele aluno já atingiu a meta, sobre o total da atividade.' : 'how many videos that student reached the target, over the activity total.'}</li>
            <li><span className="font-semibold">modulepercent</span> {pt ? 'e' : 'and'} <span className="font-semibold">averagebest:</span> {pt ? '−1 quando ainda não há tentativa com nota.' : '−1 when there is no graded attempt yet.'}</li>
            <li><span className="font-semibold">lastinteraction:</span> {pt ? 'Unix time da última interação. 0 se a pessoa ainda não começou.' : 'Unix time of the last interaction. 0 if the person has not started.'}</li>
          </ul>
        </section>

        <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <div className="flex items-center gap-2 text-amber-950">
            <Shield size={18} />
            <h2 className="text-sm font-bold">{pt ? 'Segurança' : 'Security'}</h2>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-amber-950">
            {pt
              ? 'Esta página não autentica ninguém e não lê o Moodle. O token: quem o possui vê nome, e-mail e desempenho dos alunos que aquela conta Moodle pode ver. Crie um token por integração, troque se vazar e não use a conta de administrador do site para o BI.'
              : 'The secret is the token: whoever holds it sees names, emails and performance for the students that Moodle account can see. Create one token per integration, rotate it if it leaks, and do not use the site administrator account for BI.'}
          </p>
        </section>

        <p className="text-sm text-slate-500">
          <a className="font-semibold text-sky-700 hover:underline" href="/">
            {pt ? 'Abrir o Geppetto' : 'Open Geppetto'}
          </a>
        </p>
      </main>
    </div>
  );
}
